import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { playClip, sfx, getAudioConfig } from './rpSounds';
import { chestVideoSrc } from './rpPreload';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const play = (src, vol = 0.7) => playClip(src, vol);
const WHITE_AT = 0.2; // s antes do fim: o clipe já virou clarão → revela na hora (sem esperar tela branca)
const PARTICLES = Array.from({ length: 26 }, (_, i) => ({
  a: (i / 26) * 360 + (i % 3) * 7, d: 120 + (i % 5) * 28, s: 5 + (i % 4) * 3, t: 0.9 + (i % 3) * 0.25, delay: (i % 6) * 60,
  c: ['#c084fc', '#a3e635', '#e879f9', '#4ade80', '#f5d0fe'][i % 5],
}));

const CSS = `
@keyframes rp-burst { 0% { transform: translate(-50%,-50%) scale(.2); opacity: 1 } 100% { transform: translate(-50%,-50%) scale(3.2); opacity: 0 } }
@keyframes rp-ray { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
@keyframes rp-pop { 0% { transform: scale(.4); opacity: 0; filter: brightness(4) blur(6px) } 55% { transform: scale(1.12); opacity: 1; filter: brightness(1.6) blur(0) } 100% { transform: scale(1); opacity: 1; filter: brightness(1) } }
@keyframes rp-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-8px) } }
@keyframes rp-particle { 0% { transform: translate(-50%,-50%) rotate(var(--a)) translateX(0) scale(1); opacity: 1 } 100% { transform: translate(-50%,-50%) rotate(var(--a)) translateX(var(--d)) scale(0); opacity: 0 } }
@keyframes rp-rise { 0% { transform: translateY(18px); opacity: 0 } 100% { transform: translateY(0); opacity: 1 } }
@keyframes rp-smoke { 0% { transform: translate(-50%,-30%) scale(.6) rotate(0deg); opacity: 0 } 25% { opacity: .55 } 100% { transform: translate(-50%,-140%) scale(1.9) rotate(40deg); opacity: 0 } }
@keyframes rp-neon { 0%,100% { opacity: .55; transform: translate(-50%,-50%) scale(1) } 50% { opacity: .9; transform: translate(-50%,-50%) scale(1.08) } }
`;

// Card com o clipe do baú abrindo (sem cortes); o clarão do fim do vídeo vira uma explosão dourada
// com raios e partículas, e o fantasminha "estoura" do branco para a cor real.
export const RpChestOpen = ({ result, error, onClose }) => {
  const [phase, setPhase] = useState('video'); // video | reveal
  const vidRef = useRef(null);
  const ended = useRef(false);
  const done = useRef(false);
  const flashed = useRef(false);

  useEffect(() => { if (error) onClose(); }, [error, onClose]);

  useEffect(() => {
    const v = vidRef.current;
    if (phase !== 'video' || !v) return undefined;
    const skip = () => { ended.current = true; reveal(); };
    let raf = 0;
    const tick = () => {                       // rAF (mais fino que timeupdate): dispara o clarão/revelação no frame certo
      if (!v.duration || v.ended) return;
      if (v.duration - v.currentTime <= WHITE_AT) { skip(); return; }
      raf = requestAnimationFrame(tick);
    };
    v.addEventListener('error', skip);
    v.addEventListener('playing', () => { raf = requestAnimationFrame(tick); }, { once: true });
    const cfg = getAudioConfig();                     // áudio do clipe segue "Sons do jogo" + volume
    v.muted = cfg.muted || !cfg.sfxOn || cfg.sfxVol === 0;
    v.volume = cfg.sfxVol;
    v.play().catch(() => { v.muted = true; v.play().catch(skip); });
    const t = setTimeout(skip, 6000);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); v.removeEventListener('error', skip); };
  }, [phase]);

  const reveal = () => {
    if (done.current || !ended.current || !result) return;
    done.current = true;
    if (!flashed.current) { flashed.current = true; play('/rp/sfx_flash.mp3'); }
    setPhase('reveal');
    setTimeout(() => play('/rp/sfx_win.mp3', 0.8), 250);
  };
  useEffect(() => { reveal(); }, [result]);

  const revealing = phase === 'reveal' && result;
  const waiting = phase === 'video' && ended.current && !result; // clipe acabou mas o servidor ainda não respondeu

  return createPortal(
    <div className="fixed inset-0 z-[97] flex items-center justify-center bg-black/92 p-3 backdrop-blur-sm" data-testid="rp-open-overlay">
      <style>{CSS}</style>
      <div data-testid="rp-open-card"
        className="relative aspect-square w-[94vw] max-w-md overflow-hidden rounded-3xl border-2 border-fuchsia-400/80 bg-black shadow-[0_0_80px_rgba(168,85,247,0.55)]">
        {/* vídeo inteiro, sem corte — já em memória (pré-carregado ao abrir a página) */}
        <video ref={vidRef} playsInline preload="auto" data-testid="rp-open-video" src={chestVideoSrc()}
          onEnded={() => { ended.current = true; reveal(); }}
          className="absolute inset-0 h-full w-full object-contain"
          style={{ opacity: revealing ? 0 : 1, transition: 'opacity .25s' }} />
        {!revealing && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[16%]" style={{ background: 'linear-gradient(to top, #0a0518 30%, transparent)' }} />}
        {waiting && <div className="absolute inset-0 flex items-center justify-center bg-white" data-testid="rp-open-waiting"><div className="h-10 w-10 animate-spin rounded-full border-4 border-fuchsia-300 border-t-transparent" /></div>}

        {revealing && (
          <div data-testid="rp-open-result" className="absolute inset-0 overflow-hidden"
            style={{ background: 'radial-gradient(circle at 50% 42%, #2e1065 0%, #140a2e 50%, #06030f 100%)' }}>
            {/* halo neon pulsando (roxo + verde) */}
            <div className="pointer-events-none absolute left-1/2 top-[42%] h-72 w-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(192,132,252,.75) 0%, rgba(168,85,247,.35) 35%, transparent 70%)', filter: 'blur(14px)', animation: 'rp-neon 2.8s ease-in-out infinite' }} />
            <div className="pointer-events-none absolute left-1/2 top-[42%] h-96 w-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(163,230,53,.45) 0%, rgba(74,222,128,.18) 40%, transparent 70%)', filter: 'blur(24px)', animation: 'rp-neon 3.6s .6s ease-in-out infinite' }} />
            {/* fumaça subindo */}
            {[0, 700, 1400, 2100].map((d, i) => (
              <div key={d} className="pointer-events-none absolute left-1/2 top-[70%] h-56 w-72 rounded-full"
                style={{ background: i % 2 ? 'radial-gradient(ellipse, rgba(192,132,252,.55), transparent 65%)' : 'radial-gradient(ellipse, rgba(134,239,172,.4), transparent 65%)', filter: 'blur(22px)', animation: `rp-smoke 4.2s ${d}ms ease-out infinite` }} />
            ))}
            {/* raios dourados girando */}
            <div className="pointer-events-none absolute left-1/2 top-[42%] h-[180%] w-[180%] -translate-x-1/2 -translate-y-1/2 opacity-70"
              style={{ background: 'repeating-conic-gradient(from 0deg, rgba(192,132,252,.45) 0deg 9deg, rgba(192,132,252,0) 9deg 24deg)', animation: 'rp-ray 14s linear infinite', maskImage: 'radial-gradient(circle, black 0%, transparent 62%)', WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 62%)' }} />
            {/* ondas de choque */}
            {[0, 180, 360].map((d) => (
              <div key={d} className="pointer-events-none absolute left-1/2 top-[42%] h-40 w-40 rounded-full border-4 border-fuchsia-300/80"
                style={{ animation: `rp-burst 1.1s ${d}ms ease-out forwards`, boxShadow: '0 0 40px 10px rgba(192,132,252,.6), inset 0 0 30px rgba(163,230,53,.5)' }} />
            ))}
            {/* partículas */}
            {PARTICLES.map((p, i) => (
              <span key={i} className="pointer-events-none absolute left-1/2 top-[42%] rounded-full"
                style={{ '--a': `${p.a}deg`, '--d': `${p.d}px`, width: p.s, height: p.s, background: p.c, boxShadow: `0 0 8px ${p.c}`,
                  animation: `rp-particle ${p.t}s ${p.delay}ms cubic-bezier(.1,.8,.3,1) forwards` }} />
            ))}

            <div className="relative flex h-full flex-col items-center justify-center gap-1.5 p-6 text-center">
              <div style={{ animation: 'rp-pop 900ms 120ms cubic-bezier(.16,1,.3,1) both' }}>
                <div style={{ animation: 'rp-float 2.6s 1s ease-in-out infinite' }}>
                  <img src={`/rp/${result.prop_id}.png`} alt={result.gift_name} data-testid="rp-open-prize-art"
                    className="h-44 w-44 object-contain drop-shadow-[0_0_30px_rgba(217,70,239,.95)]" />
                </div>
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.35em] text-lime-300 drop-shadow-[0_0_10px_rgba(163,230,53,.9)]" style={{ animation: 'rp-rise .5s .6s ease-out both' }}>Você ganhou</p>
              <p className="text-3xl font-black leading-tight text-white drop-shadow-[0_0_18px_rgba(217,70,239,1)]" data-testid="rp-open-prize-name" style={{ animation: 'rp-rise .5s .7s ease-out both' }}>{result.gift_name}</p>
              <p className="text-4xl font-black text-fuchsia-200 drop-shadow-[0_0_20px_rgba(192,132,252,1)]" data-testid="rp-open-prize-coins" style={{ animation: 'rp-rise .5s .8s ease-out both' }}>🪙 {fmt(result.coins)}</p>
              <p className="max-w-[85%] text-[11px] text-purple-100/85" style={{ animation: 'rp-rise .5s .95s ease-out both' }}>Abra sua live e toque em "Receber na live" para o presente chegar.</p>
              <button onClick={() => { sfx.close(); onClose(); }} data-testid="rp-open-close" style={{ animation: 'rp-rise .5s 1.1s ease-out both' }}
                className="mt-1 rounded-full border border-fuchsia-400/80 bg-black/60 px-8 py-2.5 text-[11px] font-black uppercase tracking-widest text-fuchsia-200 shadow-[0_0_18px_rgba(217,70,239,.6)] transition-transform active:scale-95">
                Continuar
              </button>
            </div>
          </div>
        )}

        {/* clarão: nasce branco junto com o fim do clipe e dissolve rápido sobre a explosão (bem menos tempo de tela branca) */}
        <div data-testid="rp-open-flash" className="pointer-events-none absolute inset-0 bg-white"
          style={{ opacity: 0, animation: revealing ? 'rp-flash .7s ease-out forwards' : 'none' }} />
        <style>{'@keyframes rp-flash { 0% { opacity: 1 } 15% { opacity: 1 } 100% { opacity: 0 } }'}</style>
      </div>
    </div>,
    document.body
  );
};

export default RpChestOpen;
